import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { BehaviorSubject } from 'rxjs';

import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MenuComponent } from '@shared/components/menu/menu.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { AuthorizeDirective } from '@shared/directives/authorize.directive';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { SpecificationInputType, SpecificationSettingType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';

import { SpecificationQuery } from './specification/specification.query';
import { SpecificationService } from './specification/specification.service';
import { SpecificationModel } from './specification/specification.store';
import {
  FullSpecificationCategory,
  SpecificationCategoryQuery,
} from './specification-category/specification-category.query';
import { SpecificationCategoryService } from './specification-category/specification-category.service';
import { SpecificationsUploadComponent } from './specifications-upload/specifications-upload.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    MenuComponent,
    IconComponent,
    AuthorizeDirective,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    FormErrorDirective,
    NgSelectComponent,
    AsyncPipe,
  ],
  selector: 'aux-specifications',
  templateUrl: './specifications.component.html',
})
export class SpecificationsComponent {
  readonly specificationCategoryQuery = inject(SpecificationCategoryQuery);

  private readonly fb = inject(UntypedFormBuilder);
  private readonly overlayService = inject(OverlayService);
  private readonly specificationCategoryService = inject(SpecificationCategoryService);
  private readonly specificationQuery = inject(SpecificationQuery);
  private readonly specificationService = inject(SpecificationService);

  showForm = signal('');

  specTypesEnum = SpecificationInputType;

  fg = this.fb.group({});
  loading$ = new BehaviorSubject(false);
  specSetTypes = Object.keys(SpecificationSettingType).map((value) => {
    return {
      label: value.replace('SPECIFICATION_', ''),
      value,
    };
  });

  constructor() {
    this.specificationCategoryService
      .getAllSpecifications$()
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  createSpecSetFB(
    { id, label, type, value } = {
      id: '',
      label: '',
      type: SpecificationSettingType.SPECIFICATION_GENERAL,
      value: '',
    }
  ) {
    return this.fb.group({
      id,
      label,
      type,
      value: [value, Validators.required],
    });
  }

  getSettingsControls() {
    const fa = this.fg.get('settings') as UntypedFormArray;

    return fa.controls;
  }

  hideForm() {
    this.fg = this.fb.group({});
    this.showForm.set('');
  }

  onAddNewLine() {
    const fa = this.fg.get('settings') as UntypedFormArray;

    fa.push(this.createSpecSetFB());
  }

  onRemoveLastLine() {
    const fa = this.fg.get('settings') as UntypedFormArray;

    fa.removeAt(fa.value.length - 1);
  }

  onRemoveSpec(id: string, cat: FullSpecificationCategory) {
    const spec = this.specificationQuery.getEntity(id) as SpecificationModel;

    this.overlayService.openPopup<
      { message: string },
      boolean | undefined,
      ConfirmationModalComponent
    >({
      content: ConfirmationModalComponent,
      data: {
        message: `Are you sure that you want to remove the Specification ${spec.name} ?`,
      },
      settings: {
        header: 'Remove Specification',
        primaryButton: {
          action: async (instance) => {
            const { success } = await this.specificationService.remove(id, cat.id);

            instance?.ref.close();

            if (success) {
              this.overlayService.success('Specification successfully removed!');
            }
          },
          label: 'Remove',
        },
      },
    });
  }

  async onSaveForm(cat: FullSpecificationCategory) {
    if (!(this.fg.valid && !this.loading$.getValue())) {
      return;
    }

    this.loading$.next(true);
    // new spec case
    const { settings, specName, specType } = this.fg.value as {
      settings: {
        id: string;
        label: string;
        type: SpecificationSettingType;
        value: string;
      }[];
      specName: string;
      specType: SpecificationInputType;
    };

    if (this.showForm() === cat.id) {
      if (specType === SpecificationInputType.INPUT_MULTIVALUE) {
        const { success } = await this.specificationService.add(
          {
            category_id: cat.id,
            input_type: SpecificationInputType.INPUT_MULTIVALUE,
            name: specName,
          },
          settings.map((set) => {
            return {
              setting_key: set.label,
              setting_value: set.value,
              specification_type: set.type,
            };
          })
        );

        if (success) {
          this.overlayService.success('New specification successfully added!');
        }
      }

      this.hideForm();
    } else {
      this.loading$.next(true);
      await this.specificationService.update(
        {
          category_id: cat.id,
          id: this.showForm(),
          input_type: specType,
          name: specName,
        },
        settings.map((x) => {
          return {
            id: x.id,
            setting_key: x.label,
            setting_value: x.value,
            specification_type: x.type,
          };
        })
      );

      this.hideForm();
    }

    this.loading$.next(false);
  }

  onShowForm(id: string, cat: FullSpecificationCategory) {
    // reset the form
    this.hideForm();

    if (id === cat.id) {
      const settings: UntypedFormArray = this.fb.array([this.createSpecSetFB()]);

      this.fg = this.fb.group({
        settings,
        specName: ['', Validators.required],
        specType: SpecificationInputType.INPUT_MULTIVALUE,
      });
    } else {
      const spec = cat.specifications.find((fullSpec) => fullSpec.id === id);

      if (spec?.input_type === SpecificationInputType.INPUT_MULTIVALUE) {
        const settings: UntypedFormArray = this.fb.array([]);

        for (const setting of spec.settings) {
          settings.push(
            this.createSpecSetFB({
              id: setting.id,
              label: setting.setting_key ?? '',
              type: setting.specification_type,
              value: setting.setting_value ?? '',
            })
          );
        }

        this.fg = this.fb.group({
          settings,
          specName: [spec.name, Validators.required],
          specType: SpecificationInputType.INPUT_MULTIVALUE,
        });
      }
    }

    this.showForm.set(id);
  }

  onSpecUploadClick() {
    type InstanceType = null | SpecificationsUploadComponent;
    this.overlayService.openPopup({
      content: SpecificationsUploadComponent,
      settings: {
        header: 'Upload Specification',
        primaryButton: {
          action: (instance: InstanceType) => instance?.onUpload(),
          label: 'Upload',
        },
      },
    });
  }
}
